import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import AdminButton from '../../../components/admin/AdminButton';
import { getFedexShipmentByTrackingNumber, cancelFedexShipment } from '../../../services/fedexApi';

const FedexShipmentDetail = () => {
  const { id } = useParams(); // id est le numéro de suivi
  const navigate = useNavigate();
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingLabel, setLoadingLabel] = useState(false);

  // Charger les détails de l'expédition
  useEffect(() => {
    const fetchShipmentDetails = async () => {
      try {
        setLoading(true);
        const data = await getFedexShipmentByTrackingNumber(id);
        if (data) {
          setShipment(data);
        } else {
          toast.error('Impossible de trouver cette expédition');
          navigate('/admin/fedex-shipments');
        }
      } catch (error) {
        console.error('Erreur lors du chargement des détails de l&apos;expédition:', error);
        toast.error('Erreur lors du chargement des détails de l&apos;expédition');
        
        // En mode développement, utiliser des données de démonstration
        if (process.env.NODE_ENV === 'development') {
          console.warn('Mode développement: utilisation de données de démonstration');
          setShipment({
            tracking_number: id || '794644915445',
            status: 'created',
            created_at: new Date().toISOString(),
            recipient_firstname: 'Jean',
            recipient_lastname: 'Dupont',
            recipient_company: 'Entreprise A',
            recipient_address: '123 Rue de Paris',
            recipient_address2: '',
            recipient_city: 'Paris',
            recipient_zipcode: '75001',
            recipient_country: 'France',
            recipient_phone: '0123456789',
            recipient_email: 'jean.dupont@example.com',
            package_type: 'FEDEX_ENVELOPE',
            package_weight: 0.5,
            package_count: 1,
            fedex_service_type: 'PRIORITY_OVERNIGHT',
            fedex_signature_option: 'INDIRECT',
            pickup_type: 'USE_SCHEDULED_PICKUP',
            fedex_ship_date: format(new Date(), 'yyyy-MM-dd'),
            send_notification: true,
            notification_email: 'jean.dupont@example.com',
            reference_number: 'REF123456',
            fedex_response: {
              transactionId: 'mock-transaction-id',
              output: {
                shipmentResults: {
                  shipmentDocuments: [
                    {
                      contentType: 'application/pdf',
                      docType: 'SHIPPING_LABEL',
                      encodedLabel: 'base64-encoded-label-would-be-here'
                    }
                  ],
                  completedShipmentDetail: {
                    masterTrackingNumber: id || '794644915445',
                    serviceDescription: {
                      serviceType: 'PRIORITY_OVERNIGHT',
                      description: 'FedEx Priority Overnight'
                    },
                    shipmentRating: {
                      shipmentRateDetails: [
                        {
                          totalNetCharge: 25.50
                        }
                      ]
                    }
                  }
                }
              }
            }
          });
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchShipmentDetails();
    }
  }, [id, navigate]);

  // Fonction pour télécharger l'étiquette d'expédition
  const handleDownloadLabel = async () => {
    try {
      setLoadingLabel(true);
      
      // Vérifier si nous avons une étiquette encodée dans la réponse
      const encodedLabel = shipment?.fedex_response?.output?.shipmentResults?.shipmentDocuments?.[0]?.encodedLabel;
      
      if (encodedLabel) {
        // Convertir la chaîne base64 en blob
        const byteCharacters = atob(encodedLabel);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        // Créer un URL pour le blob et télécharger
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `fedex-label-${shipment.tracking_number}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('Étiquette téléchargée avec succès');
      } else {
        toast.error('Aucune étiquette disponible pour cette expédition');
        
        // En mode développement, simuler un téléchargement
        if (process.env.NODE_ENV === 'development') {
          toast.success('(DEV) Simulation de téléchargement d\'étiquette');
        }
      }
    } catch (error) {
      console.error('Erreur lors du téléchargement de l\'étiquette:', error);
      toast.error('Erreur lors du téléchargement de l\'étiquette');
    } finally {
      setLoadingLabel(false);
    }
  };

  // Fonction pour annuler l'expédition
  const handleCancelShipment = async () => {
    // Demander confirmation avant d'annuler
    if (!window.confirm(`Êtes-vous sûr de vouloir annuler l&apos;expédition ${shipment.tracking_number} ? Cette action est irréversible.`)) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Appel au service pour annuler l'expédition
      await cancelFedexShipment(shipment.tracking_number);
      
      toast.success(`Expédition ${shipment.tracking_number} annulée avec succès`);
      
      // Mettre à jour l'état local
      setShipment(prev => ({
        ...prev,
        status: 'cancelled',
        cancelled_at: new Date().toISOString()
      }));
    } catch (error) {
      console.error('Erreur lors de l\'annulation de l\'expédition:', error);
      toast.error('Impossible d\'annuler l\'expédition. Veuillez réessayer plus tard.');
      
      // En mode développement, simuler une annulation réussie
      if (process.env.NODE_ENV === 'development') {
        console.warn('Mode développement: simulation d\'une annulation réussie');
        setShipment(prev => ({
          ...prev,
          status: 'cancelled',
          cancelled_at: new Date().toISOString()
        }));
        toast.success('(DEV) Expédition annulée avec succès (simulation)');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour formater la date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'dd MMMM yyyy à HH:mm', { locale: fr });
    } catch (error) {
      return dateString;
    }
  };

  // Fonction pour obtenir le libellé du statut
  const getStatusLabel = (status) => {
    const statusMap = {
      pending: 'En attente',
      created: 'Créée',
      in_transit: 'En transit',
      delivered: 'Livrée',
      cancelled: 'Annulée',
      exception: 'Exception'
    };
    return statusMap[status] || status;
  };

  // Fonction pour obtenir la classe CSS du statut
  const getStatusClass = (status) => {
    const statusClassMap = {
      pending: 'bg-yellow-100 text-yellow-800',
      created: 'bg-blue-100 text-blue-800',
      in_transit: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      exception: 'bg-orange-100 text-orange-800'
    };
    return statusClassMap[status] || 'bg-gray-100 text-gray-800';
  };

  // Fonction pour obtenir le libellé du type de service
  const getServiceTypeLabel = (serviceType) => {
    const serviceTypeMap = {
      'PRIORITY_OVERNIGHT': 'FedEx Priority Overnight',
      'STANDARD_OVERNIGHT': 'FedEx Standard Overnight',
      'FEDEX_2_DAY': 'FedEx 2 Day',
      'FEDEX_EXPRESS_SAVER': 'FedEx Express Saver',
      'FEDEX_GROUND': 'FedEx Ground',
      'INTERNATIONAL_PRIORITY': 'FedEx International Priority',
      'INTERNATIONAL_ECONOMY': 'FedEx International Economy'
    };
    return serviceTypeMap[serviceType] || serviceType;
  };

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Détails de l&apos;expédition FedEx</h1>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!shipment) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Détails de l&apos;expédition FedEx</h1>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Impossible de trouver cette expédition.
        </div>
        <div className="mt-4">
          <AdminButton
            onClick={() => navigate('/admin/fedex-shipments')}
            text="Retour à la liste"
            icon="arrow-left"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Détails de l&apos;expédition FedEx</h1>
        <div className="flex space-x-2">
          <AdminButton
            onClick={() => navigate('/admin/fedex-shipments')}
            text="Retour à la liste"
            icon="arrow-left"
          />
          {shipment.status !== 'cancelled' && (
            <AdminButton
              onClick={handleDownloadLabel}
              text={loadingLabel ? 'Téléchargement...' : 'Télécharger l\'étiquette'}
              icon="download"
              disabled={loadingLabel}
              color="green"
            />
          )}
          {shipment.status !== 'cancelled' && shipment.status !== 'delivered' && (
            <AdminButton
              onClick={handleCancelShipment}
              text="Annuler l'expédition"
              icon="x"
              color="red"
            />
          )}
        </div>
      </div>

      {/* Informations principales */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-semibold mb-2">Expédition #{shipment.tracking_number}</h2>
            <div className="flex items-center mb-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusClass(shipment.status)}`}>
                {getStatusLabel(shipment.status)}
              </span>
              {shipment.reference_number && (
                <span className="ml-2 text-gray-500">
                  Référence: {shipment.reference_number}
                </span>
              )}
            </div>
            <p className="text-gray-600">
              Créée le {formatDate(shipment.created_at)}
            </p>
            {shipment.cancelled_at && (
              <p className="text-red-600">
                Annulée le {formatDate(shipment.cancelled_at)}
              </p>
            )}
          </div>
          <div className="text-right">
            <p className="font-semibold">{getServiceTypeLabel(shipment.fedex_service_type)}</p>
            <p className="text-sm text-gray-500">
              L&apos;expédition a été créée le {format(new Date(shipment.created_at), 'PPP à HH:mm', { locale: fr })}
            </p>
            <p className="text-gray-600">
              Poids: {shipment.package_weight} kg
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Informations du destinataire */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Destinataire</h2>
          <div className="space-y-2">
            <p className="font-medium">
              {shipment.recipient_firstname} {shipment.recipient_lastname}
            </p>
            {shipment.recipient_company && (
              <p>{shipment.recipient_company}</p>
            )}
            <p>{shipment.recipient_address}</p>
            {shipment.recipient_address2 && (
              <p>{shipment.recipient_address2}</p>
            )}
            <p>
              {shipment.recipient_zipcode} {shipment.recipient_city}
            </p>
            <p>{shipment.recipient_country}</p>
            <p className="pt-2">
              <span className="font-medium">Téléphone:</span> {shipment.recipient_phone}
            </p>
            <p>
              <span className="font-medium">Email:</span> {shipment.recipient_email}
            </p>
          </div>
        </div>

        {/* Détails de l&apos;expédition */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Détails de l&apos;expédition</h2>
          <div className="space-y-2">
            <p>
              <span className="font-medium">Type de colis:</span> {shipment.package_type}
            </p>
            <p>
              <span className="font-medium">Poids:</span> {shipment.package_weight} kg
            </p>
            <p>
              <span className="font-medium">Nombre de colis:</span> {shipment.package_count}
            </p>
            <p>
              <span className="font-medium">Service FedEx:</span> {getServiceTypeLabel(shipment.fedex_service_type)}
            </p>
            <p>
              <span className="font-medium">Option de signature:</span> {shipment.fedex_signature_option}
            </p>
            <p>
              <span className="font-medium">Type d&apos;enlèvement:</span> {shipment.pickup_type}
            </p>
            <p>
              <span className="font-medium">Notification:</span> {shipment.send_notification ? 'Oui' : 'Non'}
            </p>
            {shipment.send_notification && shipment.notification_email && (
              <p>
                <span className="font-medium">Email de notification:</span> {shipment.notification_email}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Historique de suivi (à implémenter ultérieurement) */}
      <div className="bg-white shadow rounded-lg p-6 mt-6">
        <h2 className="text-lg font-semibold mb-4">Historique de suivi</h2>
        <p className="text-gray-500 italic">
          L&apos;historique de suivi détaillé sera disponible dans une prochaine version.
        </p>
      </div>
    </div>
  );
};

export default FedexShipmentDetail;
